import { CustomTimeframe, UnresolvedTimeframe } from '@squaredup/timeframes';
import RelativeDateTimeField from './RelativeDateTimeField';

import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';

import { Button, TooltipButton } from '@/components/Button';
import Text from '@/components/Text';
import { faCircleQuestion, faThumbTack } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Day } from 'date-fns';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useFormChange } from 'ui/editor/dataStream/hooks/useFormChange';
import CalendarSettings from './CalendarSettings';
import { TimeframeFormData, TimeframeFormSchema } from './timeframeForm';
import {
    getCustomTimeframeFormDefaults,
    getTimeframePreview,
    toCustomTimeframeOptions,
    toCustomTimeframeValue
} from './timeframeFormUtils';

/**
 * Renders a form allowing a user to select a custom timeframe.
 *
 * User can set a start and end, using relative or fixed times.
 */
export const CustomTimeframeForm = ({
    onChange,
    currentTimeframe,
    isCurrentDefaultTimeframe = false,
    onDefaultTimeframeChange,
    container,
    showCalendarSettings
}: {
    onChange: (timeframe: UnresolvedTimeframe) => void;
    /** The timeframe to use when pre-filling the form options. */
    currentTimeframe?: UnresolvedTimeframe;
    /** Whether the current timeframe is the default timeframe, i.e. should user be able to pin it? */
    isCurrentDefaultTimeframe?: boolean;
    container?: HTMLElement | null;
    onDefaultTimeframeChange?: (timeframe: UnresolvedTimeframe) => void;
    /** Whether to show the calendar settings, i.e. week and year start options. */
    showCalendarSettings?: boolean;
}) => {
    const currentTimeframeFormValues = getCustomTimeframeFormDefaults(currentTimeframe);
    const [defaultValues] = useState(currentTimeframeFormValues);

    const formProps = useForm<TimeframeFormData>({
        defaultValues,
        mode: 'all',
        reValidateMode: 'onChange',
        resolver: zodResolver(TimeframeFormSchema)
    });

    const {
        handleSubmit,
        formState: { errors, isValid, isSubmitting },
        watch,
        trigger,
        reset
    } = formProps;

    const isFormValid = Object.keys(errors).length === 0 && isValid;

    const handleOnSubmit = async (data: TimeframeFormData, setAsDefault?: boolean) => {
        const start = toCustomTimeframeValue(data.from);
        const end = toCustomTimeframeValue(data.to);
        const options = toCustomTimeframeOptions(data.weekStartsOn, data.yearStartsOn);

        const newTimeframe: CustomTimeframe = {
            start,
            end,
            type: 'custom',
            options
        };

        onChange(newTimeframe);

        // if true will also pin the current timeframe as the default
        if (setAsDefault && onDefaultTimeframeChange) {
            onDefaultTimeframeChange(newTimeframe);
        }
    };

    /**
     * Reset the form to the currently applied timeframe.
     */
    const handleOnReset = () => {
        reset(currentTimeframeFormValues);
    };

    const formValues = watch();
    const fromTimeframe = toCustomTimeframeValue(formValues.from);
    const toTimeframe = toCustomTimeframeValue(formValues.to);
    const currentFromTimeframe = toCustomTimeframeValue(currentTimeframeFormValues.from);
    const currentToTimeframe = toCustomTimeframeValue(currentTimeframeFormValues.to);
    // Check if form values are different from currently applied timeframe
    const hasTimeframeChanged = fromTimeframe + '/' + toTimeframe !== currentFromTimeframe + '/' + currentToTimeframe;
    const hasCalendarChanged = !isEqual(
        {
            weekStartsOn: formValues.weekStartsOn,
            yearStartsOn: formValues.yearStartsOn
        },
        {
            weekStartsOn: currentTimeframeFormValues.weekStartsOn,
            yearStartsOn: currentTimeframeFormValues.yearStartsOn
        }
    );

    const hasChanged = hasTimeframeChanged || hasCalendarChanged;

    useFormChange(watch, () => {
        // ensure validation is triggered on every change
        trigger();
    });

    useEffect(() => {
        // validate the form when the component mounts, in case the options has become invalid over time.
        trigger();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Generate the date previews for the "From" and "To" fields.
     */
    const datePreviews = useMemo(
        () =>
            getTimeframePreview(
                fromTimeframe,
                toTimeframe,
                toCustomTimeframeOptions(formValues.weekStartsOn, formValues.yearStartsOn)
            ),
        [fromTimeframe, toTimeframe, formValues.weekStartsOn, formValues.yearStartsOn]
    );

    /**
     * Can the user pin the current custom timeframe as the default?
     */
    const canSetAsDefaultTimeframe = isFormValid && (!isCurrentDefaultTimeframe || hasChanged);

    return (
        <FormProvider {...formProps}>
            <form
                onSubmit={handleSubmit((data) => handleOnSubmit(data))}
                className='flex flex-col flex-grow mt-4'
                data-testid='customTimeframeForm'
            >
                <div className='flex flex-col flex-grow px-3'>
                    <div style={{ minHeight: '228px' }}>
                        <RelativeDateTimeField
                            name='from'
                            label='From'
                            container={container}
                            defaultFixedValue={new Date(new Date().setHours(0, 0, 0, 0))}
                            weekStartsOn={formValues.weekStartsOn as Day}
                        />
                        {errors.from && (
                            <Text.Body className='mt-2 text-statusErrorPrimary' data-testid='fromError'>
                                {errors.from.message ||
                                    errors.from.modifierValue?.message ||
                                    'Fill in all required fields.'}
                            </Text.Body>
                        )}
                        {!errors.from && formValues.from.type !== 'fixed' && (
                            <Text.Body className='mt-2 text-textSecondary' data-testid='fromPreview'>
                                {datePreviews.from} <RoundingTooltipButton />
                            </Text.Body>
                        )}
                        <RelativeDateTimeField
                            name='to'
                            label='To'
                            container={container}
                            defaultFixedValue={new Date(new Date().setHours(23, 59, 0, 0))}
                            weekStartsOn={formValues.weekStartsOn as Day}
                        />
                        {errors.to && (
                            <Text.Body className='mt-2 text-statusErrorPrimary' data-testid='toError'>
                                {errors.to.message ||
                                    errors.to.modifierValue?.message ||
                                    'Fill in all required fields.'}
                            </Text.Body>
                        )}
                        {!errors.to && formValues.to.type !== 'fixed' && (
                            <Text.Body className='mt-2 mb-4 text-textSecondary' data-testid='toPreview'>
                                {datePreviews.to} <RoundingTooltipButton />
                            </Text.Body>
                        )}
                    </div>
                    {showCalendarSettings && (
                        <CalendarSettings yearStartMonth={formValues.yearStartsOn?.month} className='mt-3' />
                    )}
                </div>
                <div className='flex items-center justify-end p-3 gap-x-4 bg-modalFooter text-textPrimary'>
                    {hasChanged && (
                        <Button
                            onClick={handleOnReset}
                            variant='tertiary'
                            data-testid='resetButton'
                            disabled={isSubmitting}
                        >
                            Reset
                        </Button>
                    )}
                    {onDefaultTimeframeChange && currentTimeframe && (
                        <TooltipButton
                            type='button'
                            variant='secondary'
                            onClick={handleSubmit((data) => handleOnSubmit(data, true))}
                            disabled={!canSetAsDefaultTimeframe || isSubmitting}
                            className='px-3'
                            title={canSetAsDefaultTimeframe && 'Set default timeframe'}
                            data-testid='setDefaultTimeframeButton'
                            icon={<FontAwesomeIcon icon={faThumbTack} fixedWidth />}
                        />
                    )}
                    <Button
                        type='submit'
                        variant='primary'
                        disabled={!isFormValid || !hasChanged || isSubmitting}
                        data-testid='applyButton'
                    >
                        Apply
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};

/**
 * Tooltip button to show information about rounding of timeframes.
 */
export const RoundingTooltipButton = () => (
    <TooltipButton
        variant='tertiary'
        icon={<FontAwesomeIcon icon={faCircleQuestion} fixedWidth />}
        className='ml-1 text-textSecondary'
        title={
            <p data-testid='roundingTooltip'>
                The selected timeframe shown on tiles may be rounded and adjusted for caching, aggregation, and
                performance reasons.{' '}
                <a
                    href='https://docs.squaredup.com/first-steps/timeframes#automatic-time-range-adjustments'
                    className='text-textLink hover:underline focus:underline'
                    target='_blank'
                    rel='noopener noreferrer'
                >
                    Learn more
                </a>
            </p>
        }
        data-testid='roundingTooltipButton'
    />
);

/**
 * Header to show above the custom timeframe form, with documentation link.
 */
export const CustomTimeframeHeader = () => {
    return (
        <div className='px-3'>
            <Text.H3 className='mt-0'>Custom</Text.H3>
            <Text.Body className='text-textSecondary'>
                Learn more about{' '}
                <a
                    href='https://docs.squaredup.com/first-steps/timeframes#custom-timeframes'
                    className='text-textLink hover:underline focus:underline'
                    target='_blank'
                    rel='noopener noreferrer'
                >
                    custom timeframes.
                </a>
            </Text.Body>
            <hr className='mt-1.5 border-dividerPrimary' />
        </div>
    );
};
